import React, { useState, useRef, useEffect } from 'react';
import TryIcon from '@mui/icons-material/Try';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Divider,
  CircularProgress,
  List,
  ListItem,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import 'prismjs/prism'; // Prismオブジェクトをグローバルに公開
import 'prismjs/themes/prism.css';
import 'prismjs/components/prism-python';

// 型定義
export interface Message {
  sender: 'user' | 'ai';
  text: string;
  username?: string; // Add username property
  timestamp: string; // NEW FIELD: Add timestamp
  category?: string; // NEW FIELD: Add category
}

export interface HistoryContent {
  id: number;
  summary_history_id: number;
  section_type: string;
  content: string;
  created_at: string;
  updated_at: string;
}

interface AiAssistantProps {
  pdfSummaryContent?: string;
  summaryId?: number;
  viewMode: 'new' | 'history' | 'current'; // 再追加
  currentMessages: Message[];
  onMessagesChange: (messages: Message[]) => void;
  currentPdfFilePaths?: number[]; // now array of SharedFile IDs
  username: string | null; // Add username prop
  authToken?: string | null; // NEW: Add authToken prop (allow null)
}

const AiAssistant = ({ pdfSummaryContent, summaryId, viewMode, currentMessages, onMessagesChange, currentPdfFilePaths, username, authToken }: AiAssistantProps) => {
  const [input, setInput] = useState('');
  const [displayMessages, setDisplayMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const handleSend = async (messageToSend?: string, displayMessage?: string) => {

    const message = messageToSend || input;
    if (!message.trim()) return;

    // チャット画面に表示するメッセージ
    const userDisplayMessage: Message = { sender: 'user', text: displayMessage || message, username: username || undefined, timestamp: new Date().toISOString() }; // timestampを追加
    const newMessages = [...currentMessages, userDisplayMessage];
    onMessagesChange(newMessages); // 親コンポーネントに通知
    setDisplayMessages(newMessages); // 即座に表示を更新
    setInput('');
    setLoading(true);

    try {
      const requestBody: any = {
        message: message,
        pdf_summary: pdfSummaryContent,
        summary_id: summaryId,
        parent_summary_id: summaryId, // Add parent_summary_id
      };

      // summaryIdが存在しない場合のみoriginal_file_pathsを追加
      if (summaryId === undefined) {
        // Backend accepts both int/str, but normalize to string to avoid 422
        requestBody.original_file_paths = currentPdfFilePaths?.map(String);
      }

      const response = await fetch(`${API_BASE}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      const aiMessage: Message = { sender: 'ai', text: data.reply, timestamp: new Date().toISOString() }; // timestampを追加
      const finalMessages = [...newMessages, aiMessage];
      onMessagesChange(finalMessages); // 親コンポーネントに通知

      // 質問と回答のペアをバックエンドに保存
      console.log(viewMode);
      if (summaryId && viewMode !== 'history') { // summaryIdがある場合のみ保存
        const userQuestion = userDisplayMessage.text;
        const aiAnswer = data.reply;
        const category = userDisplayMessage.category || 'その他';

        try {
          await fetch(`${API_BASE}/api/save-question-summary`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authToken}`, // NEW: Authorization ヘッダーを追加
            },
            body: JSON.stringify({
              summary_history_id: summaryId,
              section_type: 'user_question_summary',
              content: '', // 要約はバックエンドで生成されるため空 (ユーザー提供の要約がない場合、バックエンドでAI生成される)
              question_text: userQuestion,
              ai_answer_text: aiAnswer,
              user_provided_summary: null, // NEW FIELD: ユーザーが提供する要約 (現時点ではnull)
            }),
          });
          console.log('質問単位の要約が保存されました');
        } catch (saveError) {
          console.error('質問単位の要約保存中にエラーが発生しました:', saveError);
        }
      }

    } catch (error) {
      console.error('Error fetching AI response:', error);
      const errorMessage: Message = {
        sender: 'ai',
        text: '申し訳ありません。エラーが発生しました。',
        timestamp: new Date().toISOString(), // timestampを追加
      };
      const errorMessages = [...newMessages, errorMessage];
      onMessagesChange(errorMessages); // 親コンポーネントに通知
    } finally {
      setLoading(false);
    }
  };

  // 表示用メッセージの管理
  useEffect(() => {
    console.log("AiAssistant useEffect triggered. currentMessages:", currentMessages);
    setDisplayMessages(currentMessages);
  }, [currentMessages]);

  // メッセージの追加時に一番下にスクロール
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
    }
  }, [displayMessages]);

  return (
    <Paper
      sx={{
        height: '100%',
        p: 2,
        border: '1px solid #00bcd4',
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 0 15px rgba(0, 188, 212, 0.7)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <TryIcon color="secondary" />
        <Typography variant="h6" component="h2">
          AI Assistant
        </Typography>
      </Box>
      <Divider sx={{ mb: 2, borderColor: '#00bcd4' }} />

      <Box sx={{ flexGrow: 1, overflowY: 'auto', mb: 2 }} ref={messagesEndRef}>
        <List>
          {displayMessages.map((msg, index) => (
            <ListItem key={index} sx={{ display: 'flex', flexDirection: 'column', alignItems: msg.sender === 'user' ? 'flex-end' : 'flex-start', px: 0 }}>
              {msg.sender === 'user' && msg.username && (
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, mr: 1 }}>
                  {msg.username}
                </Typography>
              )}
              <Paper
                elevation={2}
                sx={{
                                    p: 1.5,
                  bgcolor: msg.sender === 'user' ? 'primary.main' : 'background.paper', // AIメッセージの背景色をテーマのpaper色に
                  color: msg.sender === 'user' ? 'primary.contrastText' : 'text.primary',
                  maxWidth: '80%',
                  border: '1px solid #00bcd4', // メッセージのボーダー
                  boxShadow: '0 0 5px rgba(0, 188, 212, 0.5)', // メッセージのシャドウ
                }}
              >
                <ReactMarkdown
                  components={{
                    p: ({ children }) => <p style={{ margin: 0 }}>{children}</p>,
                    pre: ({ children }) => (
                      <pre style={{
                        backgroundColor: '#1a1a2e', // ダークな背景色
                        color: '#e0e0e0', // 明るい文字色
                        border: '1px solid #00bcd4', // サイバーチックなボーダー
                        borderRadius: '4px',
                        padding: '10px',
                        overflowX: 'auto', // 横スクロール
                        boxShadow: '0 0 5px rgba(0, 188, 212, 0.5)', // サイバーチックなシャドウ
                        whiteSpace: "break-spaces"
                      }}>
                        {children}
                      </pre>
                    ),
                    code: ({ children }) => (
                      <code style={{
                        fontFamily: '"Share Tech Mono", monospace', // サイバーチックなフォント
                        fontSize: '0.9em',
                      }}>
                        {children}
                      </code>
                    ),
                    a: ({ children }) => (
                      <a style={{
                        wordBreak: "break-all",
                        color: "#00bcd4"
                      }} href={children as string}>
                        {children}
                      </a>
                    ),
                }}>
                  {msg.text}
                </ReactMarkdown>
              </Paper>
            </ListItem>
          ))}
          {loading && (
            <ListItem sx={{ justifyContent: 'center' }}>
              <CircularProgress />
            </ListItem>
          )}
        </List>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-around', mb: 2 }}>
            <Button variant="outlined" onClick={() => handleSend('要約された文章からpythonで疑似的に動作させるコードを生成してください。使えるライブラリは組み込みライブラリのみです．', 'Pythonコード生成')}>
              Pythonコード生成
            </Button>
            <Button variant="outlined" onClick={() => handleSend('用語を解説してください', '用語解説')}>
              用語解説
            </Button>
            <Button variant="outlined" onClick={() => handleSend('要約内容が正しいかwebで検索してください', '要約内容チェック')}>
              要約内容チェック
            </Button>
          </Box>
          <Box sx={{ display: 'flex' }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="メッセージを入力..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && !loading && handleSend()}
              disabled={loading}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleSend()}
              disabled={loading}
              sx={{ ml: 1 }}
            >
              Send
            </Button>
          </Box>
    </Paper>
  );
};

export default AiAssistant;
const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
